import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import React, { useState } from 'react';
import { useClient } from '../contexts/ClientContext';
import { StarIcon, ShieldIcon, CheckCircleIcon, XIcon, CrownIcon, MessageCircleIcon, LockIcon } from './Icons';

interface PremiumModalProps {
    onClose: () => void;
    initialPack?: '50' | '100' | '150';
    onProceedToPurchase?: (pack: '50' | '100' | '150') => void;
}

const PremiumModal: React.FC<PremiumModalProps> = ({ 
    onClose, 
    initialPack = '100',
    onProceedToPurchase
}) => {
    const { client, openAuthModal } = useClient();
    const [selectedPack, setSelectedPack] = useState<'50' | '100' | '150'>(initialPack && ['50', '100', '150'].includes(initialPack) ? initialPack : '100');

    const handleConfirmPurchase = () => {
        onClose();
        if (onProceedToPurchase) {
            onProceedToPurchase(selectedPack);
        } else if (client) {
            window.dispatchEvent(new CustomEvent('astro_open_purchase_chat', { detail: { pack: selectedPack } }));
        } else {
            openAuthModal();
        }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4 overflow-y-auto animate-fade-in">
            <div className="relative w-full h-full sm:h-auto max-h-[100dvh] sm:max-h-[95vh] max-w-3xl overflow-y-auto bg-gradient-to-b from-mystic-900 via-mystic-950 to-black border-0 sm:border-2 border-gold-500/60 rounded-none sm:rounded-3xl p-5 sm:p-7 shadow-[0_0_50px_rgba(251,191,36,0.25)] text-white space-y-4 sm:space-y-6 custom-scrollbar flex flex-col justify-between">
                
                {/* Bouton Fermer */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all cursor-pointer"
                    title="Fermer"
                >
                    <XIcon className="w-5 h-5" />
                </button>

                {/* En-tête */}
                <div className="text-center space-y-2">
                    <div className="inline-flex items-center gap-2 bg-black/60 border border-gold-500/40 text-gold-400 text-xs font-bold uppercase px-3.5 py-1.5 rounded-full shadow-md">
                        <img src="/transcash.png" alt="Transcash" className="h-4 sm:h-5 object-contain" />
                        <span>Recharges Transcash (1 € = 10 Astres)</span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white">
                        Choisissez votre Recharge Consultation
                    </h2>
                    <p className="text-sm text-gray-300 max-w-lg mx-auto">
                        Créditez instantanément vos <strong>Astres permanents (sans date d'expiration)</strong> pour appeler nos médiums en direct sur le site (20 Astres/min).
                    </p>
                </div>

                {/* Sélection des 4 packs */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Pack 50€ */}
                    <div 
                        onClick={() => setSelectedPack('50')}
                        className={`cursor-pointer rounded-2xl p-4 border-2 transition-all flex flex-col justify-between ${
                            selectedPack === '50' 
                                ? 'bg-gold-500/15 border-gold-400 shadow-[0_0_20px_rgba(251,191,36,0.2)]' 
                                : 'bg-white/5 border-white/10 hover:border-white/20'
                        }`}
                    >
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-[11px] font-black uppercase text-gold-400">Ticket 50 €</span>
                                <span className="text-[9px] bg-amber-500 text-black font-bold px-1.5 py-0.2 rounded">+50 Offerts</span>
                            </div>
                            <div className="text-2xl font-bold font-serif text-white">50€</div>
                            <p className="text-xs text-amber-300 font-bold mt-1">550 Astres</p>
                            <p className="text-[11px] text-gray-400">27,5 min d'appel direct</p>
                        </div>
                        <div className="mt-3 pt-2 border-t border-white/10 text-center">
                            <span className="text-[10px] text-gold-400 font-bold">Sélectionner 50€</span>
                        </div>
                    </div>

                    {/* Pack 100€ */}
                    <div 
                        onClick={() => setSelectedPack('100')}
                        className={`cursor-pointer rounded-2xl p-4 border-2 transition-all flex flex-col justify-between relative overflow-hidden ${
                            selectedPack === '100' 
                                ? 'bg-gradient-to-b from-amber-500/20 to-gold-500/30 border-gold-400 shadow-[0_0_25px_rgba(251,191,36,0.3)]' 
                                : 'bg-white/5 border-white/10 hover:border-white/20'
                        }`}
                    >
                        <div className="absolute top-0 right-0 bg-red-600 text-white text-[8px] font-black uppercase px-2 py-0.5 rounded-bl">
                            Populaire
                        </div>
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-[11px] font-black uppercase text-gold-300">Ticket 100 €</span>
                            </div>
                            <div className="text-2xl font-bold font-serif text-white">100€</div>
                            <p className="text-xs text-gold-300 font-bold mt-1">1 150 Astres</p>
                            <p className="text-[11px] text-gray-300">57,5 min d'appel direct</p>
                        </div>
                        <div className="mt-3 pt-2 border-t border-white/10 text-center">
                            <span className="text-[10px] text-gold-300 font-bold">+150 Astres Offerts</span>
                        </div>
                    </div>

                    {/* Pack 150€ */}
                    <div 
                        onClick={() => setSelectedPack('150')}
                        className={`cursor-pointer rounded-2xl p-4 border-2 transition-all flex flex-col justify-between ${
                            selectedPack === '150' 
                                ? 'bg-purple-900/30 border-purple-400 shadow-[0_0_25px_rgba(168,85,247,0.3)]' 
                                : 'bg-white/5 border-white/10 hover:border-white/20'
                        }`}
                    >
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-[11px] font-black uppercase text-purple-300">Ticket 150 €</span>
                                <span className="text-[9px] bg-purple-500 text-black font-bold px-1.5 py-0.2 rounded">+300 Offerts</span>
                            </div>
                            <div className="text-2xl font-bold font-serif text-white">150€</div>
                            <p className="text-xs text-purple-300 font-bold mt-1">1 800 Astres</p>
                            <p className="text-[11px] text-gray-400">1h30 d'appel direct</p>
                        </div>
                        <div className="mt-3 pt-2 border-t border-white/10 text-center">
                            <span className="text-[10px] text-purple-300 font-bold">Sélectionner 150€</span>
                        </div>
                    </div>
                </div>

                {/* Bannière Sécurité Transcash */}
                <div className="bg-black/60 border border-gold-500/30 rounded-2xl p-4 space-y-3">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-gold-500/10 rounded-xl border border-gold-500/20 text-gold-400 shrink-0">
                            <LockIcon className="w-5 h-5" />
                        </div>
                        <div className="text-xs text-gray-300 space-y-1">
                            <div className="font-bold text-white flex items-center gap-2 flex-wrap">
                                <span>Paiement Sécurisé & 100% Anonyme</span>
                                <img src="/transcash.png" alt="Transcash" className="h-4 object-contain inline-block" />
                                <span className="bg-green-500/20 text-green-300 text-[10px] px-1.5 py-0.5 rounded border border-green-500/30">Garanti</span>
                            </div>
                            <p className="text-[11px] leading-relaxed">
                                Les Astres achetés via Transcash <strong>n'expirent jamais</strong>. Notre secrétariat (Sarah) valide votre ticket en direct pour créditer votre portefeuille instantanément.
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={handleConfirmPurchase}
                        className="w-full bg-gradient-to-r from-gold-600 via-gold-500 to-yellow-400 hover:from-gold-500 hover:to-yellow-300 text-black font-extrabold py-3.5 rounded-xl uppercase tracking-wider text-xs transition-all shadow-[0_0_20px_rgba(251,191,36,0.3)] flex items-center justify-center gap-2 cursor-pointer hover:scale-[1.01] active:scale-95"
                    >
                        <MessageCircleIcon className="w-4 h-4" />
                        Démarrer l'Assistance Secrétariat & Activer ({selectedPack}€)
                    </button>
                </div>

            </div>
        </div>
    );
};

export default PremiumModal;
